const CLOUD_PRIMARY = "https://hoarding-life-rpg.onrender.com";
const DEFAULT_LOCAL_PRIMARY = "http://localhost:5001";
const DEFAULT_LOCAL_FALLBACKS = [
  "http://localhost:5000",
  "http://127.0.0.1:5001",
  "http://127.0.0.1:5000",
];
const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "0.0.0.0"]);
const inFlightGetRequests = new Map();
const responseCache = new Map();

const normalizeBaseUrl = (value) => {
  const normalized = String(value || "").trim().replace(/\/+$/, "");
  return normalized || null;
};

const isLocalBaseUrl = (value) => {
  try {
    return LOCAL_HOSTS.has(new URL(value).hostname);
  } catch (_error) {
    return false;
  }
};

const isLocalRuntime = () => {
  if (typeof window === "undefined") return true;
  return LOCAL_HOSTS.has(window.location.hostname);
};

const toFallbackList = (value) =>
  String(value || "")
    .split(",")
    .map((entry) => normalizeBaseUrl(entry))
    .filter(Boolean);

export const getBackendBaseUrls = () => {
  const envPrimary = normalizeBaseUrl(process.env.REACT_APP_BACKEND_URL);
  const envFallbacks = toFallbackList(process.env.REACT_APP_BACKEND_FALLBACK_URLS);
  const localRuntime = isLocalRuntime();
  const primary =
    !localRuntime && (!envPrimary || isLocalBaseUrl(envPrimary))
      ? CLOUD_PRIMARY
      : envPrimary || DEFAULT_LOCAL_PRIMARY;
  const deployedFallbacks = localRuntime
    ? []
    : [CLOUD_PRIMARY, ...envFallbacks.filter((baseUrl) => !isLocalBaseUrl(baseUrl))];
  const localFallbacks = localRuntime ? DEFAULT_LOCAL_FALLBACKS : [];
  const combined = [primary, ...deployedFallbacks, ...localFallbacks].filter(Boolean);

  return Array.from(new Set(combined));
};

const parseJsonSafe = async (response) => {
  const raw = await response.text();
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_error) {
    return raw;
  }
};

const createBackendHttpError = ({ payload, response, baseUrl }) => {
  const error = new Error(
    (payload && payload.message) || `Request failed with status ${response.status}`
  );
  error.status = response.status;
  error.payload = payload;
  error.baseUrl = baseUrl;
  error.isBackendHttpError = true;
  return error;
};

export const isBackendUnavailableError = (error) =>
  [502, 503, 504].includes(Number(error?.status || 0));

export const isBackendRequestAbortError = (error) => error?.name === "AbortError";

const createAbortError = () => {
  const error = new Error("The request was aborted.");
  error.name = "AbortError";
  return error;
};

const getAbortReason = (signal) =>
  signal?.reason instanceof Error ? signal.reason : createAbortError();

const waitForRequest = (request, signal) => {
  if (!signal) return request;
  if (signal.aborted) return Promise.reject(getAbortReason(signal));

  return new Promise((resolve, reject) => {
    const handleAbort = () => reject(getAbortReason(signal));
    signal.addEventListener("abort", handleAbort, { once: true });

    request.then(
      (value) => {
        signal.removeEventListener("abort", handleAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", handleAbort);
        reject(error);
      }
    );
  });
};

const waitForSharedRequest = (requestKey, entry, signal) => {
  entry.consumers += 1;
  return waitForRequest(entry.promise, signal).finally(() => {
    entry.consumers = Math.max(0, entry.consumers - 1);
    if (!entry.settled && entry.consumers === 0) {
      if (inFlightGetRequests.get(requestKey) === entry) {
        inFlightGetRequests.delete(requestKey);
      }
      entry.controller.abort(createAbortError());
    }
  });
};

const normalizeHeadersForKey = (headers) => {
  if (!headers) return [];

  let entries;
  if (typeof headers.entries === "function") {
    entries = Array.from(headers.entries());
  } else if (Array.isArray(headers)) {
    entries = headers;
  } else {
    entries = Object.entries(headers);
  }

  return entries
    .map(([name, value]) => [String(name).toLowerCase(), String(value)])
    .sort(([left], [right]) => left.localeCompare(right));
};

const createGetRequestKey = (path, init, fallbackStatusCodes, timeoutMs, cacheTtlMs) =>
  JSON.stringify({
    path: String(path || ""),
    headers: normalizeHeadersForKey(init.headers),
    credentials: init.credentials || "",
    mode: init.mode || "",
    cache: init.cache || "",
    fallbackStatusCodes,
    timeoutMs,
    cacheTtlMs,
  });

const executeBackendRequest = async (path, init, fallbackStatusCodes) => {
  const bases = getBackendBaseUrls();
  let lastError = null;

  for (const baseUrl of bases) {
    const normalizedPath = String(path || "").startsWith("/") ? path : `/${path}`;
    const targetUrl = `${baseUrl}${normalizedPath}`;
    try {
      const response = await fetch(targetUrl, init);
      const payload = await parseJsonSafe(response);

      if (response.ok) {
        return { data: payload, baseUrl, status: response.status };
      }

      const shouldFallback = fallbackStatusCodes.includes(response.status);
      const error = createBackendHttpError({ payload, response, baseUrl });
      lastError = error;

      if (!shouldFallback) {
        throw error;
      }
    } catch (error) {
      if (isBackendRequestAbortError(error)) {
        throw error;
      }
      if (error?.isBackendHttpError && !fallbackStatusCodes.includes(error.status)) {
        throw error;
      }
      lastError = error;
    }
  }

  throw lastError || new Error("No backend candidates were reachable.");
};

const executeWithTimeout = async (path, init, fallbackStatusCodes, timeoutMs) => {
  const normalizedTimeoutMs = Math.max(0, Number(timeoutMs) || 0);
  if (!normalizedTimeoutMs) {
    return executeBackendRequest(path, init, fallbackStatusCodes);
  }

  const controller = new AbortController();
  const externalSignal = init.signal;
  const handleExternalAbort = () => controller.abort(getAbortReason(externalSignal));
  const timeoutId = setTimeout(
    () => controller.abort(createAbortError()),
    normalizedTimeoutMs
  );

  if (externalSignal) {
    if (externalSignal.aborted) {
      clearTimeout(timeoutId);
      throw getAbortReason(externalSignal);
    }
    externalSignal.addEventListener("abort", handleExternalAbort, { once: true });
  }

  try {
    return await executeBackendRequest(
      path,
      { ...init, signal: controller.signal },
      fallbackStatusCodes
    );
  } finally {
    clearTimeout(timeoutId);
    externalSignal?.removeEventListener("abort", handleExternalAbort);
  }
};

export const clearBackendRequestCache = (pathPrefix = "") => {
  const normalizedPrefix = String(pathPrefix || "");
  for (const [key, entry] of responseCache.entries()) {
    if (!normalizedPrefix || entry.path.startsWith(normalizedPrefix)) {
      responseCache.delete(key);
    }
  }
};

export const requestBackendJson = async (
  path,
  init = {},
  {
    fallbackStatusCodes = [404, 502, 503, 504],
    coalesce = true,
    cacheTtlMs = 0,
    timeoutMs = 0,
  } = {}
) => {
  const method = String(init.method || "GET").toUpperCase();
  const isShareableGet = method === "GET" && !init.body && coalesce;
  const callerSignal = init.signal;

  if (!isShareableGet) {
    const result = await executeWithTimeout(path, init, fallbackStatusCodes, timeoutMs);
    if (method !== "GET") {
      clearBackendRequestCache();
    }
    return result;
  }

  const requestKey = createGetRequestKey(
    path,
    init,
    fallbackStatusCodes,
    timeoutMs,
    cacheTtlMs
  );
  const cached = responseCache.get(requestKey);
  if (cached) {
    if (cached.expiresAt > Date.now()) {
      return waitForRequest(Promise.resolve(cached.value), callerSignal);
    }
    responseCache.delete(requestKey);
  }

  let entry = inFlightGetRequests.get(requestKey);
  if (!entry) {
    const controller = new AbortController();
    const sharedInit = { ...init, signal: controller.signal };
    entry = {
      consumers: 0,
      controller,
      promise: null,
      settled: false,
    };

    entry.promise = executeWithTimeout(
      path,
      sharedInit,
      fallbackStatusCodes,
      timeoutMs
    )
      .then((result) => {
        const normalizedCacheTtlMs = Math.max(0, Number(cacheTtlMs) || 0);
        if (normalizedCacheTtlMs) {
          responseCache.set(requestKey, {
            path: String(path || ""),
            expiresAt: Date.now() + normalizedCacheTtlMs,
            value: result,
          });
        }
        return result;
      })
      .finally(() => {
        entry.settled = true;
        if (inFlightGetRequests.get(requestKey) === entry) {
          inFlightGetRequests.delete(requestKey);
        }
      });
    inFlightGetRequests.set(requestKey, entry);
  }

  return waitForSharedRequest(requestKey, entry, callerSignal);
};
